import { useState, useRef, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import type { FileAttachment } from "@/components/chat/types";

export function useFileAttachments() {
  const { toast } = useToast();
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const maxSize = 8 * 1024 * 1024;
    const maxTotal = 8 * 1024 * 1024;
    const newAttachments: FileAttachment[] = [];
    let runningTotal = attachments.reduce((sum, a) => sum + (a.size || 0), 0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > maxSize) {
        toast({ title: `File ${file.name} is too large (max 8MB)`, variant: "destructive" });
        continue;
      }
      if (runningTotal + file.size > maxTotal) {
        toast({ title: `Attachments exceed the 8MB total limit`, description: `${file.name} was not added.`, variant: "destructive" });
        continue;
      }
      runningTotal += file.size;

      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });

      newAttachments.push({
        id: crypto.randomUUID(),
        name: file.name,
        type: file.type,
        size: file.size,
        url: dataUrl,
      });
    }

    setAttachments((prev) => [...prev, ...newAttachments]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [toast]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  return {
    attachments,
    setAttachments,
    fileInputRef,
    handleFileSelect,
    removeAttachment,
    clearAttachments,
  };
}
